import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AccessGuard from 'components/shared/AccessGuard';
import Alert from 'components/shared/Alert';
import { FilledBadge } from 'components/shared/Badge';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDateAtTime } from 'lib/date';
import { PRODUCT_ACCESSORS } from 'models/Profile';
import Workflow, { WorkflowAction, WorkflowGroupOptions, WorkflowLookupOption, WorkflowStatus } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import BadgeCheckIcon from 'svg/BadgeCheckIcon';
import BadgeXIcon from 'svg/BadgeXIcon';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';
import EllipsisIcon from 'svg/EllipsisIcon';
import SelectedCheckmarkIcon from 'svg/SelectedCheckmarkIcon';

import { RowButton } from './categoryCard/styles';
import ChangeSelectedFacility from './syncPatient/ChangeSelectedFacility';
import { OUTCOME_DENIED, OUTCOME_NOT_SELECTED, OUTCOME_SELECTED, recordedOutcome } from './constants';
import { completionAttribution, sortedGroupsByName } from './FanoutPanel';
import {
  deniedItself,
  inHouseWinnerReason,
  MAX_NOT_SELECTED_DETAIL,
  resolveNotSelectedReason,
} from './notSelectedReason';

// The always-available Referral Outcome (OLIO-2611): Selected or Not Selected, and on Selected,
// which facility. The step sits quietly at Pending on the API side (never demands attention), but
// this panel is interactive the whole time — a coordinator records Not Selected the moment the
// patient chooses elsewhere. The facility list shows every targeted building, alpha ordered, each
// with its current facility-decision pill — informing the pick rather than filtering it
// (deliberately permissive; the API doesn't enforce it either).
//
// Not Selected carries why (OLIO-2736): a reason, then a competitor when the reason says one won
// it, then the competitor's name in free text when the one picked can't be reported on. Which
// reason owes a competitor and which competitor owes a name are declared BY THE LOOKUP ITEMS
// (`requiresCompetitor` / `requiresDetail`), never by slugs hardcoded here — a client shipping its
// own lists nominates its own triggers.
//
// The vocabulary itself (and the legacy won/lost normalization) lives in ./constants, because the
// list's Current Status column reads the outcome too (OLIO-3172).

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: Record<string, string>) => void;
  isCompleting: boolean;
  // Changing an already-recorded decision (OLIO-2736). The form is the same one — a referral being
  // changed to Not Selected owes a reason exactly as much as one recorded that way first time — so
  // it only differs in which side it opens on, what the button says, and that it amends.
  startOn?: string;
  submitLabel?: string;
  onCancel?: () => void;
}

export default function ReferralOutcomePanel({
  action,
  workflow,
  onComplete,
  isCompleting,
  startOn,
  submitLabel = 'Confirm',
  onCancel,
}: Props) {
  const [outcome, setOutcome] = useState<string | null>(startOn ?? null);
  const [facilityId, setFacilityId] = useState('');
  const [reason, setReason] = useState<WorkflowLookupOption | null>(null);
  const [competitor, setCompetitor] = useState<WorkflowLookupOption | null>(null);
  const [competitorName, setCompetitorName] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const { canEditAction } = useWorkflowPermissions();

  // Nothing here is readable content: the copy instructs someone to record the outcome and every control below writes
  // it, which the API's policy refuses for a viewer. So the panel goes whole rather than losing its buttons — a form
  // with no way to submit it is worse than an absent one. canEditAction, not blanket canEdit, so the specialist holding
  // only workflow_action_accept_referral_edit still records the outcome — the same primitive the completed card's
  // Change to Selected / Not Selected menus use.
  if (!canEditAction(action, workflow)) return null;

  const facilities = sortedGroupsByName(workflow.groups);
  // Nothing to ask when the referral went to one building — it is the answer.
  const onlyFacility = facilities.length === 1 ? facilities[0] : undefined;
  const reasons = workflow.lookupOptions('not_selected_reasons');
  const competitors = workflow.lookupOptions('competitors');
  const needsCompetitor = !!reason?.requiresCompetitor;
  const needsCompetitorName = needsCompetitor && !!competitor?.requiresDetail;
  // Deliberately absent from `notSelectedReady` below: this box is offered, never demanded
  // (OLIO-3419), so leaving it empty has to keep Confirm live.
  const offersDetail = !!reason?.offersDetail;

  // The same decision-chip look every other Accepted/Denied badge on this page uses
  // (FilledBadge — FacilityDecisionPanel's `decision-chip`, ReferralOutcomeResult's row badges
  // below), rather than the differently-styled Pill this dropdown used to draw its options with
  // (OLIO-3234). Pending gets no chip, matching FacilityDecisionPanel: an undecided facility
  // shows no badge there either.
  const decisionBadge = (groupId: string): ReactNode => {
    const value = workflow.valueFor('facility_determination', { groupId })?.value;
    if (value === 'accepted')
      return (
        <FilledBadge color='green' icon={<BadgeCheckIcon />}>
          Accepted
        </FilledBadge>
      );
    if (value === 'denied')
      return (
        <FilledBadge color='red' icon={<BadgeXIcon />}>
          Denied
        </FilledBadge>
      );
    return null;
  };

  const selectedFacilityId = onlyFacility?.id ?? facilityId;
  const selectedFacilityName = facilities.find((group) => group.id === selectedFacilityId)?.name;
  // Every requirement the API enforces, mirrored so Confirm is never a button that fails.
  const notSelectedReady =
    !!reason && (!needsCompetitor || !!competitor) && (!needsCompetitorName || !!competitorName.trim());
  const canSave =
    !isCompleting &&
    ((outcome === OUTCOME_NOT_SELECTED && notSelectedReady) || (outcome === OUTCOME_SELECTED && !!selectedFacilityId));

  const save = () => {
    if (!outcome) return;
    if (outcome === OUTCOME_SELECTED) {
      onComplete(action.id, { final_outcome: OUTCOME_SELECTED, selected_facility_id: selectedFacilityId });
      return;
    }
    onComplete(action.id, {
      final_outcome: OUTCOME_NOT_SELECTED,
      not_selected_reason: reason?.apiName ?? '',
      // Only what this reason actually calls for — the API drops contradictions, but sending them
      // would still be claiming something the person never chose.
      ...(needsCompetitor && competitor ? { competitor: competitor.apiName } : {}),
      ...(needsCompetitorName ? { competitor_name: competitorName.trim() } : {}),
      // Sent whenever the reason offered a box, empty included: an empty one is a real answer here
      // ("they went to an assisted living, I was never told which"), and the API stores blank as
      // nothing rather than as "".
      ...(offersDetail ? { not_selected_detail: reasonDetail.trim() } : {}),
    });
  };

  // Entering a side starts it fresh: picks made under "Not Selected" must not survive a
  // trip through "Selected" and reappear pre-filled — a stale reason is one Confirm away
  // from being recorded as if it were chosen today. Re-clicking the active side is not a
  // reset, so a double-click can't wipe someone's picks.
  const chooseOutcome = (choice: string) => {
    if (choice === outcome) return;

    if (choice === OUTCOME_NOT_SELECTED) {
      setReason(null);
      setCompetitor(null);
      setCompetitorName('');
      setReasonDetail('');
    } else {
      setFacilityId('');
    }
    setOutcome(choice);
  };

  const chooseReason = (option: WorkflowLookupOption | null) => {
    setReason(option);
    // A reason that no longer asks for a competitor must not keep the last one's answer.
    if (!option?.requiresCompetitor) {
      setCompetitor(null);
      setCompetitorName('');
    }
    // Same rule for the typed detail: a reason that does not offer one must not carry the last
    // reason's answer, or somebody who changes their mind files it under a reason that never asked.
    if (!option?.offersDetail) setReasonDetail('');
  };

  return (
    <Wrap data-testid='referral-outcome-panel'>
      <BodySmall>
        Confirm the outcome of this referral below by indicating whether the patient selected your facility.
      </BodySmall>
      {/* A change-gesture form is LOCKED to the side the gesture named (OLIO-2973): "Change to Not
          Selected" owes a reason, and rendering the toggle here would hand the person the Selected
          side — a warning-less second door to the winner move that belongs to the Change selected
          facility modal (with its in-flight/already-synced warnings). First-time recording keeps
          the toggle. */}
      {!startOn && (
        <Choices>
          <ChoiceButton
            type='button'
            $selected={outcome === OUTCOME_SELECTED}
            onClick={() => chooseOutcome(OUTCOME_SELECTED)}
            data-testid='outcome-won'>
            <IconWrap $color={colors.green100}>
              <SelectedCheckmarkIcon width={28} height={28} />
            </IconWrap>
            Selected
          </ChoiceButton>
          <ChoiceButton
            type='button'
            $selected={outcome === OUTCOME_NOT_SELECTED}
            onClick={() => chooseOutcome(OUTCOME_NOT_SELECTED)}
            data-testid='outcome-lost'>
            <IconWrap $color={colors.red100}>
              <CircleXIconOutlined width={28} height={28} />
            </IconWrap>
            Not Selected
          </ChoiceButton>
        </Choices>
      )}

      {outcome === OUTCOME_NOT_SELECTED && (
        <>
          <FacilityField>
            <BodySmall>Reason</BodySmall>
            <Select<WorkflowLookupOption>
              options={reasons}
              value={reason}
              onChange={chooseReason}
              getOptionValue={(option) => option.apiName}
              getOptionLabel={(option) => option.displayName}
              placeholder='Select a reason this referral was not selected'
              dataTestId='outcome-reason'
              menuPortalTarget={document.body}
              menuPlacement='auto'
              maxMenuHeight={240}
            />
          </FacilityField>

          {needsCompetitor && (
            <FacilityField>
              <BodySmall>Competitor</BodySmall>
              <Select<WorkflowLookupOption>
                options={competitors}
                value={competitor}
                onChange={(option) => {
                  setCompetitor(option);
                  if (!option?.requiresDetail) setCompetitorName('');
                }}
                getOptionValue={(option) => option.apiName}
                getOptionLabel={(option) => option.displayName}
                placeholder='Select competitor'
                dataTestId='outcome-competitor'
                menuPortalTarget={document.body}
                menuPlacement='auto'
                maxMenuHeight={240}
              />
            </FacilityField>
          )}

          {/* The rest of the reason's own answer, when the reason names a kind of destination rather
              than which one (OLIO-3419). Optional on purpose — the label says so, because an empty
              box that silently blocks Confirm is the failure this is shaped to avoid. Capped to the
              API's own limit so a long paste is stopped here rather than by a 422. */}
          {offersDetail && (
            <FacilityField>
              <BodySmall>Detail (optional)</BodySmall>
              <Input
                value={reasonDetail}
                onChange={(event) => setReasonDetail(event.target.value)}
                placeholder='Add more detail, if you know it'
                maxLength={MAX_NOT_SELECTED_DETAIL}
                data-testid='outcome-reason-detail'
              />
            </FacilityField>
          )}

          {/* Free text because "Other" can't be reported on. What gets typed here is mined later for
              names that recur often enough to become real options. */}
          {needsCompetitorName && (
            <FacilityField>
              <BodySmall>Competitor name</BodySmall>
              <Input
                value={competitorName}
                onChange={(event) => setCompetitorName(event.target.value)}
                placeholder='Enter the competitor’s name'
                data-testid='outcome-competitor-name'
              />
            </FacilityField>
          )}
        </>
      )}

      {outcome === OUTCOME_SELECTED && !onlyFacility && (
        <FacilityField>
          <BodySmall>Select which facility the patient selected</BodySmall>
          <Select<WorkflowGroupOptions>
            options={facilities}
            value={facilities.find((group) => group.id === facilityId) ?? null}
            onChange={(group) => setFacilityId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            formatOptionLabel={(group, meta) => {
              if (meta?.context === 'value') return group.name;
              return (
                <OptionRow>
                  {group.name}
                  {decisionBadge(group.id)}
                </OptionRow>
              );
            }}
            placeholder={workflow.groups[0]?.groupType?.displayName ?? 'Select a facility'}
            dataTestId='outcome-facility'
            // The card clips overflow, so the open menu must portal out of it; near the
            // bottom of the page it flips upward instead of growing the document.
            menuPortalTarget={document.body}
            menuPlacement='auto'
            maxMenuHeight={240}
          />
        </FacilityField>
      )}

      {/* Confirming a Selected outcome creates the patient in that facility's queue (OLIO-3146). Say
          so before the coordinator confirms — an info note naming the facility they picked, once one
          is chosen. */}
      {outcome === OUTCOME_SELECTED && selectedFacilityName && (
        <Alert data-testid='outcome-selected-queue-note'>
          <Alert.Text>
            <strong>Note</strong>: Confirming this referral will add the patient to the Queue for {selectedFacilityName}
          </Alert.Text>
        </Alert>
      )}

      {/* Cancel wears the row's button sizing too — it only appears beside the submit (the amend
          form), and a full-size Cancel next to the restyled submit reads as the primary action. */}
      <ConfirmRow>
        {onCancel && (
          <ConfirmButton variant='outline-gray' onClick={onCancel} data-testid='outcome-cancel'>
            Cancel
          </ConfirmButton>
        )}
        <ConfirmButton disabled={!canSave} onClick={save} data-testid='outcome-save'>
          {submitLabel}
        </ConfirmButton>
      </ConfirmRow>
    </Wrap>
  );
}

// The completed state (Figma): the selected facility up front, and the ones that weren't behind a
// disclosure — a referral sent to five buildings shouldn't lead with four rows of "Not Selected".
//
// Each Not Selected building carries its referral's reason, "Reason – <reason>: <detail>", under the
// row's attribution (OLIO-3357, Figma 10347-28836) — a closed referral should explain itself without
// reopening the outcome step. (This reverses the earlier decision to keep the reason off the card and
// leave it to the activity log; max confirmed the reason belongs here.) The reason is referral-level,
// so it shows only when one is present — a Selected or all-Denied outcome carries none and shows nothing.
export function ReferralOutcomeResult({
  action,
  workflow,
  onAmend,
  isCompleting,
  onUpdateValues,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onAmend?: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting?: boolean;
  onUpdateValues?: (values: WorkflowValuesInput) => Promise<unknown>;
}) {
  const [showOthers, setShowOthers] = useState(false);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { canEditAction } = useWorkflowPermissions();
  // Changing TO not-selected reopens the form, because the reason is required either way — the API
  // enforces it on amends too, and on a closed referral the amend reopens the run (OLIO-2973).
  // Changing TO selected doesn't need one — the facility whose row was used is the answer — so it
  // amends straight away.
  const [changingToNotSelected, setChangingToNotSelected] = useState(false);
  const outcome = recordedOutcome(workflow);
  const winnerId = workflow.valueFor('selected_facility_id')?.value;
  const recordedFacilityId = action.outputFor('selected_facility_id');
  // The Not Selected reason to headline the card (OLIO-3357). Null on a Selected/Denied outcome,
  // which carry no reason — so this is also the gate that keeps it off those cards.
  const notSelectedReason = resolveNotSelectedReason(workflow);

  // Attribution has TWO possible sources, and using the wrong one misreports who did what (OLIO-2846).
  //
  // `completionAttribution` describes who completed AcceptReferral. That is right for the outcome
  // itself — and for every losing building, whose verdict has not changed. But the selected facility
  // can be MOVED afterwards by a plain value write, and crediting the original decision for a change
  // it was not part of would name the wrong person beside the new winner. So the WON row reads the
  // selected_facility_id value's own attribution whenever that value post-dates the action.
  const selectedFacility = workflow.valueFor('selected_facility_id');
  const selectedFacilityMoved = !!recordedFacilityId && !!winnerId && String(recordedFacilityId) !== String(winnerId);
  const updated = completionAttribution(action);
  // null when nobody is named (a machine write) — better an absent line than "Updated on <date>" with
  // no actor, which reads as though the system decided the facility.
  const movedAttribution =
    selectedFacilityMoved && selectedFacility?.createdByName && selectedFacility.updatedAt
      ? `Facility changed by ${selectedFacility.createdByName} on ${simpleDateAtTime(selectedFacility.updatedAt)}`
      : null;
  // The queue record the Selected completion created (OLIO-2666). The button renders only
  // when it would actually work: the record is live (a queue removal / later change archives
  // it), the row is the building the record was created at (an amended winner keeps the old
  // record until OLIO-2667's archive-and-recreate), and the viewer holds that building (the
  // patient page bounces anyone who doesn't). locationEpisodeId can be null; the patient
  // page then auto-selects the latest unarchived stay.
  const createdPatient = workflow.createdPatient;
  const viewPatientGroupId =
    createdPatient &&
    !createdPatient.archived &&
    createdPatient.groupId &&
    profile.canAccessGroup(createdPatient.groupId)
      ? createdPatient.groupId
      : null;
  const patientPath = createdPatient
    ? `/patients/${createdPatient.episodeId}` +
      (createdPatient.locationEpisodeId ? `?locationEpisodeId=${createdPatient.locationEpisodeId}` : '')
    : '';

  // Denied (OLIO-3170): the API recorded it when every facility denied, so there are no per-building
  // Selected / Not Selected rows to show and no outcome menu — the correction path is the facility
  // rows themselves (changing a decision reopens the referral), not this card. One line: what
  // happened and when, and where the referral now lives.
  if (outcome === OUTCOME_DENIED) {
    const at = action.completedAt ? simpleDateAtTime(action.completedAt) : null;
    const when = at ? ` on ${at}` : '';
    return (
      <ResultRows data-testid='referral-outcome-result'>
        <ResultRow>
          <ResultMain>
            <BodySmall data-testid='outcome-denied'>
              <BodySmallBold as='span'>Referral marked as denied{when}.</BodySmallBold> Denied referrals can continue to
              be accessed through the History tab on the Inbound Referrals page.
            </BodySmall>
          </ResultMain>
        </ResultRow>
      </ResultRows>
    );
  }

  // Recorded under a vocabulary this panel can't render as facility rows (e.g. 'accepted' with no
  // winner) — show what was recorded rather than badging every building Not Selected.
  if (outcome !== OUTCOME_NOT_SELECTED && !(outcome === OUTCOME_SELECTED && winnerId)) {
    return (
      <ResultRows data-testid='referral-outcome-result'>
        <ResultRow>
          <ResultMain>
            <ResultName>Recorded outcome: {outcome || 'unknown'}</ResultName>
            {updated && <ResultUpdated>{updated}</ResultUpdated>}
          </ResultMain>
        </ResultRow>
      </ResultRows>
    );
  }

  if (changingToNotSelected && onAmend) {
    return (
      <ReferralOutcomePanel
        action={action}
        workflow={workflow}
        // Close only on SUCCESS. The amend can be refused now (409 superseded, 422 not-reopenable —
        // OLIO-2973), and the page's handler swallows failures into undefined — so a truthy result is
        // the success signal. Closing unconditionally would throw away the person's reason/competitor
        // picks at the exact moment the refusal toast needs them to still be on screen.
        onComplete={async (actionId, outputs) => {
          const result = await onAmend(actionId, outputs);
          if (result) setChangingToNotSelected(false);
        }}
        isCompleting={!!isCompleting}
        startOn={OUTCOME_NOT_SELECTED}
        submitLabel='Save change'
        onCancel={() => setChangingToNotSelected(false)}
      />
    );
  }

  const facilities = sortedGroupsByName(workflow.groups);
  const winner = facilities.find((group) => group.id === winnerId);
  const others = facilities.filter((group) => group.id !== winnerId);

  // The facility whose row the menu was opened from becomes the selected one — that's what makes
  // the action meaningful on a per-facility row rather than a single button on the panel.
  const changeToSelected = (groupId: string) =>
    onAmend?.(action.id, { final_outcome: OUTCOME_SELECTED, selected_facility_id: groupId });

  // Which change the ⋯ menu offers follows the Figma annotations, and the split is what keeps
  // the two correction affordances from contradicting each other (OLIO-2973): on a NOT SELECTED
  // referral every row offers "Change to Selected" — the row names the facility that becomes the
  // answer. On a SELECTED referral only the winner's row carries a menu ("Change to Not
  // Selected"), because moving the winner to a different building already belongs to the
  // "Change selected facility" modal below, with its in-flight/already-synced warnings — a
  // second, warning-less door to the same move would be the contradiction. On a closed referral
  // both amends still work: the API reopens the run for the decision (OLIO-2973).
  //
  // Hidden, not just inert, when it cannot work: for a viewer the API's policy would 403 —
  // canEditAction, not blanket canEdit, because the amend rides the kind's own grant
  // too (the specialist holding only workflow_action_accept_referral_edit; the same primitive
  // every per-action affordance uses) — on a canceled/archived referral (Workflow::Reopen refuses
  // those outright;
  // re-deciding an archived referral is reprocessing's job), and while an amend is in flight
  // (the fire-and-forget Change to Selected has no other double-click guard).
  const rowMenu = (groupId: string, selected: boolean) => {
    if (!onAmend || !canEditAction(action, workflow) || isCompleting) return null;
    if (workflow.status === WorkflowStatus.CANCELED) return null;
    if (outcome === OUTCOME_SELECTED && !selected) return null;

    return (
      <Menu $size='small'>
        <Menu.Trigger>
          <MenuButton type='button' aria-label='Outcome options' data-testid='outcome-menu'>
            <EllipsisIcon />
          </MenuButton>
        </Menu.Trigger>
        <Menu.Content position='right'>
          {selected ? (
            <Menu.Item data-testid='menu-change-to-not-selected' onClick={() => setChangingToNotSelected(true)}>
              <Menu.Icon>
                <CircleXIconOutlined width={14} height={14} />
              </Menu.Icon>
              Change to Not Selected
            </Menu.Item>
          ) : (
            <Menu.Item data-testid='menu-change-to-selected' onClick={() => changeToSelected(groupId)}>
              <Menu.Icon>
                <SelectedCheckmarkIcon width={14} height={14} />
              </Menu.Icon>
              Change to Selected
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu>
    );
  };

  // The acting client IS the referral's client, so it is the honest name for an in-house loss. That
  // is enforced rather than assumed, which is worth citing because the sentence is only true while it
  // holds: the api refuses to target a group the run's client does not own (`Workflow::ScopedGroups`
  // raises GroupsOutOfScope, so a referral's buildings are single-client by construction), the
  // workflow policy scope matches groups on the viewer's own `scoped_client_id` (the same column
  // `actingClient` reports), and WorkflowScopeGate re-checks before this page mounts, switching the
  // acting client or bouncing rather than rendering a mismatch.
  //
  // The payload carries no client of its own, so there is no safer source being passed over here.
  // The wording is shared with Insights so the two cannot drift.
  const inHouseReason = inHouseWinnerReason(profile.actingClient?.name);

  const row = (name: string, groupId: string, selected: boolean) => {
    const menu = rowMenu(groupId, selected);
    const showViewPatient = selected && viewPatientGroupId === groupId;

    return (
      <ResultRow key={groupId} data-testid='outcome-row' data-group-id={groupId}>
        <ResultMain>
          <ResultHeader>
            <ResultName>{name}</ResultName>
            <FilledBadge color={selected ? 'green' : 'red'} icon={selected ? <BadgeCheckIcon /> : <BadgeXIcon />}>
              {selected ? 'Selected' : 'Not Selected'}
            </FilledBadge>
          </ResultHeader>
          {/* The SELECTED row reports the move when there was one; a not-selected building's verdict is
              still the outcome action's, so it keeps that attribution. Grafted onto this row helper
              because OLIO-2736's rewrite replaced the per-building map this used to live in — without it
              the winner row credits whoever recorded the outcome for a facility change they were not part
              of, naming the wrong person beside the new winner.

              Keyed on whether the facility MOVED, not on whether the move has a named author: keying on
              movedAttribution lets an unnamed move fall through to `updated`, which is precisely the
              misattribution this exists to prevent. An absent line is the correct answer there. */}
          {selected && selectedFacilityMoved
            ? movedAttribution && (
                <ResultUpdated data-testid='facility-moved-attribution'>{movedAttribution}</ResultUpdated>
              )
            : updated && <ResultUpdated>{updated}</ResultUpdated>}
          {/* The Not Selected reason (OLIO-3357), under each not-selected building's attribution.
              It's a referral-level value, so every not-selected row shows the same line. Gated on the
              outcome being Not Selected — not just on the value being absent — so a losing row on a
              Selected outcome never shows a reason even if one weren't cleared (mirrors SNFSlideout's
              outcomeRow, which guards the same way rather than trusting the tombstone). The API does
              tombstone it on an amend to Selected, so this is defense in depth. */}
          {!selected && outcome === OUTCOME_NOT_SELECTED && notSelectedReason && (
            <RowReason data-testid='outcome-reason-recorded'>
              Reason – {notSelectedReason.reasonLabel}
              {/* Both are reachable at once: a reason may ask which competitor won AND offer a
                  typed detail, and nothing stops an admin ticking both. Appending each with its own
                  ": " read as "reason: detail: competitor", so the second follow-up joins with the
                  separator the slideout and the activity feed already use. */}
              {notSelectedReason.typedDetail ? `: ${notSelectedReason.typedDetail}` : ''}
              {notSelectedReason.detail
                ? `${notSelectedReason.typedDetail ? ' · ' : ': '}${notSelectedReason.detail}`
                : ''}
            </RowReason>
          )}
          {/* A losing building on a WON referral. It reported nothing before, which read as
              though nobody knew why it lost, when the reason is the most commercially important one
              there is: the patient stayed inside this organisation. There is no stored reason to show
              here and deliberately so: `drop_contradictions!` nils `not_selected_reason` on a Selected
              outcome, so "not selected while the referral still names a winner" IS the reason, derived
              rather than recorded, and it cannot drift out of step with the winner.
              Named, not generic, because "chose a different facility" reads as a loss to a competitor:
              the opposite result.
              Suppressed for a building that DENIED the referral: the projection marks every non-winner
              not_selected whatever its own determination (api `facility_outcomes_for`), so a building
              that refused the patient lands here too, and telling it the patient chose elsewhere states
              the wrong thing about it. It falls back to the bare "Not Selected" badge it showed before
              this line existed. SNFSlideout#outcomeRow has always guarded the same way. */}
          {!selected && outcome === OUTCOME_SELECTED && !deniedItself(workflow, groupId) && (
            <RowReason data-testid='outcome-reason-in-house'>Reason – {inHouseReason}</RowReason>
          )}
        </ResultMain>
        {(menu || showViewPatient) && (
          <ResultActions>
            {showViewPatient && (
              <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
                <RowButton
                  variant='outline-primary'
                  data-testid='view-patient-in-queue'
                  onClick={() => navigate(patientPath)}>
                  View Patient in Queue
                </RowButton>
              </AccessGuard>
            )}
            {menu}
          </ResultActions>
        )}
      </ResultRow>
    );
  };

  return (
    <ResultRows data-testid='referral-outcome-result'>
      {winner && row(winner.name, winner.id, true)}

      {/* Not selected anywhere: every facility is in the same state, so there is no winner to lead
          with and nothing to hide behind a disclosure. */}
      {!winner && others.map((group) => row(group.name, group.id, false))}

      {winner && others.length > 0 && (
        <Disclosure>
          This referral was marked as “Not Selected” for {others.length}{' '}
          {others.length === 1 ? 'facility' : 'facilities'}.{' '}
          <DisclosureLink type='button' onClick={() => setShowOthers((open) => !open)} data-testid='toggle-others'>
            {showOthers ? 'Hide facilities' : 'View facilities'}
          </DisclosureLink>
        </Disclosure>
      )}
      {winner && showOthers && others.map((group) => row(group.name, group.id, false))}
      {/* A won referral's selected facility is editable AFTER the fact — the outcome result used to be
          read-only, and `amend` is a no-op on a decided run, so before this a coordinator who moved a
          patient had no way to record it and therefore no way to sync to the right EHR facility. Only
          rendered when a values-writer is supplied, so read-only call sites (tests, stories) are
          unaffected.

          SELECTED only (OLIO-2667). This used to render on a Not Selected referral too, where
          "change the selected facility" is a question with no subject — and naming a building there
          would place a patient in a building the referral says nobody won.

          Not while the created record is archived, either. Changing the facility moves the patient,
          and there is nothing to move once the record has been withdrawn or removed from the queue;
          the honest next step there is to re-record the decision. A referral with NO record is
          different and still offers this — a referral decided before the queue record existed gains
          one when its facility is named.

          And not on a CANCELED referral (OLIO-2973, found in QA): a referral archived after its
          decision keeps a completed outcome card, but a canceled run never reopens — the write
          would move a real patient on a referral the client filed away, and nothing downstream
          would ever re-derive. Same rule the per-row menus follow. */}
      {onUpdateValues &&
        workflow.groups.length > 1 &&
        outcome === OUTCOME_SELECTED &&
        !createdPatient?.archived &&
        !workflow.isLocked &&
        workflow.status !== WorkflowStatus.CANCELED && (
          <ChangeSelectedFacility workflow={workflow} onUpdateValues={onUpdateValues} />
        )}
    </ResultRows>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ChoiceButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 24px;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  background: ${({ $selected }) => ($selected ? colors.blue10 : colors.white)};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
`;

// Both icons draw with currentColor (the check via its fill default, the X via stroke).
const IconWrap = styled.span<{ $color: string }>`
  display: inline-flex;
  color: ${({ $color }) => $color};
`;

const ResultRows = styled.div`
  display: flex;
  flex-direction: column;
`;

// The Not Selected reason line on a facility row (OLIO-3357, Figma 10347-28836): regular weight,
// under the gray attribution line. Slightly separated from it, per the mockup.
const RowReason = styled(BodySmall)`
  margin-top: 8px;
`;

const ResultRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const ResultMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const ResultActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ResultName = styled(BodySmallBold)`
  font-size: 14px;
`;

const ResultUpdated = styled(BodySmall)`
  color: ${colors.black50};
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.white};
  cursor: pointer;
`;

const Disclosure = styled(BodySmall)`
  padding: 12px 0;
  color: ${colors.black75};
  border-top: 1px solid ${colors.black5};
`;

const DisclosureLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
`;

const OptionRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ConfirmRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
`;

const ConfirmButton = styled(Button)`
  font-size: 14px;
  padding: 8px 16px;
`;

const FacilityField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;
